import {
  Children,
  type ComponentProps,
  isValidElement,
  type ReactNode,
} from 'react';
import ReactMarkdown from 'react-markdown';
import clsx from 'clsx';
import remarkGfm from 'remark-gfm';

import { ChatChart } from './ChatChart';
import {
  chatChartTypeFromFence,
  isChatChartFence,
  parseChatChartRecipe,
} from './chatChartRecipe';
import {
  firstCellText,
  isTableTotalRowLabel,
  markdownNodeText,
} from './markdownTable';
import { rehypeNumericTableCells } from './rehypeNumericTableCells';

import styles from './MarkdownText.module.scss';

type ReactMarkdownProps = ComponentProps<typeof ReactMarkdown>;
type MarkdownComponents = NonNullable<ReactMarkdownProps['components']>;

export type MarkdownTextProps = {
  children: string;
  className?: string;
  components?: MarkdownComponents;
  remarkPlugins?: ReactMarkdownProps['remarkPlugins'];
};

const rehypePlugins = [
  rehypeNumericTableCells(styles['table-cell--numeric']),
];

function chartFromMarkdownCode(
  className: string | undefined,
  children: ReactNode
) {
  const language = /language-([a-z0-9-]+)/i.exec(className ?? '')?.[1];
  const text = markdownNodeText(children).replace(/\n$/, '');
  if (
    !isChatChartFence(language) &&
    language !== 'json' &&
    !text.trimStart().startsWith('{')
  ) {
    return null;
  }

  return parseChatChartRecipe(text, chatChartTypeFromFence(language));
}

const tableComponents: MarkdownComponents = {
  table: ({ children, className, node, ...props }) => (
    <div className={styles['table-scroll']}>
      <table className={clsx(styles['table'], className)} {...props}>
        {children}
      </table>
    </div>
  ),
  tr: ({ children, className, node, ...props }) => (
    <tr
      className={
        clsx(
          isTableTotalRowLabel(firstCellText(children)) &&
            styles['table-row--total'],
          className
        ) || undefined
      }
      {...props}
    >
      {children}
    </tr>
  ),
  // Charts are recognized here rather than on `code` so only fenced blocks can
  // become a chart: this runs before the fence's `code` child renders, and
  // inline code has no `pre` parent to promote it.
  pre: ({ children }) => {
    const child = Children.toArray(children).find((node) =>
      isValidElement(node)
    );
    if (
      isValidElement(child) &&
      typeof child.props === 'object' &&
      child.props != null
    ) {
      const props = child.props as {
        children?: ReactNode;
        className?: string;
      };
      const recipe = chartFromMarkdownCode(props.className, props.children);
      if (recipe) {
        return <ChatChart recipe={recipe} />;
      }
    }
    return <pre>{children}</pre>;
  },
};

export function MarkdownText({
  children,
  className,
  components,
  remarkPlugins,
}: MarkdownTextProps) {
  return (
    <div className={clsx(styles['markdown'], className)}>
      <ReactMarkdown
        components={{ ...tableComponents, ...components }}
        rehypePlugins={rehypePlugins}
        remarkPlugins={[remarkGfm, ...(remarkPlugins ?? [])]}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
